import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, Card, Badge, Button, Spinner, EmptyState } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Calculator, Download, FileText, ExternalLink, ShieldCheck, FileDown } from "lucide-react";
import { API_BASE } from "@/lib/api";

function openDossier(valuation) {
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>M&A Dossier — ${valuation.company_name}</title>
<style>
  @page { size: A4; margin: 20mm; }
  body { font-family: 'Georgia', 'Times New Roman', serif; color: #111; margin: 0; padding: 40px; background: #fff; max-width: 800px; margin: auto; }
  .cover { text-align: center; padding: 80px 20px; border-bottom: 3px solid #C5A059; margin-bottom: 40px; }
  .cover h1 { font-size: 40px; margin: 0 0 16px; letter-spacing: -0.02em; }
  .cover .sub { color: #666; font-size: 14px; letter-spacing: 0.2em; text-transform: uppercase; }
  .cover .company { font-size: 26px; margin-top: 60px; font-style: italic; color: #C5A059; }
  .kpi-row { display: flex; gap: 16px; margin: 40px 0; flex-wrap: wrap; }
  .kpi { flex: 1 1 180px; border: 1px solid #ddd; padding: 12px 16px; }
  .kpi .lbl { font-size: 10px; text-transform: uppercase; color: #666; letter-spacing: 0.15em; }
  .kpi .val { font-size: 22px; font-weight: 600; margin-top: 4px; color: #152A55; }
  h1 { font-size: 28px; margin: 30px 0 12px; padding-bottom: 8px; border-bottom: 2px solid #C5A059; }
  h2 { font-size: 20px; margin: 28px 0 12px; color: #152A55; }
  h3 { font-size: 15px; margin: 18px 0 8px; color: #333; }
  p { line-height: 1.65; font-size: 13px; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
  th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
  th { background: #152A55; color: #fff; }
  ul { margin: 10px 0 10px 20px; font-size: 13px; line-height: 1.65; }
  .footer { margin-top: 60px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 10px; color: #999; text-align: center; }
  .verdict { padding: 16px; background: linear-gradient(135deg, #152A55 0%, #1e3a72 100%); color: #fff; border-left: 4px solid #C5A059; margin: 20px 0; }
  .verdict .verdict-lbl { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #C5A059; }
  .verdict .verdict-txt { font-size: 15px; margin-top: 6px; line-height: 1.6; }
</style>
</head><body>
<div class="cover">
  <div class="sub">CLARA · Institutional M&A Dossier</div>
  <h1>Acquisition Due Diligence<br/>& Valuation Report</h1>
  <div class="company">${valuation.company_name}</div>
  <div style="margin-top:40px;font-size:11px;color:#999;">Prepared ${new Date().toLocaleDateString()} · Confidential</div>
</div>

<div class="kpi-row">
  <div class="kpi"><div class="lbl">Enterprise Value</div><div class="val">€${Math.round(valuation.enterprise_value).toLocaleString()}</div></div>
  <div class="kpi"><div class="lbl">EV/EBITDA</div><div class="val">${valuation.ev_ebitda_multiple}x</div></div>
  <div class="kpi"><div class="lbl">Recommended Bid</div><div class="val">€${Math.round(valuation.recommended_bid).toLocaleString()}</div></div>
  <div class="kpi"><div class="lbl">Credit Rating</div><div class="val">${valuation.credit_rating}</div></div>
</div>

<div class="verdict">
  <div class="verdict-lbl">Deal Feasibility: ${valuation.deal_feasibility.toUpperCase()}</div>
  <div class="verdict-txt">${valuation.summary || "See analysis below."}</div>
</div>

<div class="dossier-html">${valuation.dossier_html || "<p>Dossier generation in progress.</p>"}</div>

<div class="footer">
  CLARA — Institutional AI Operating System · This dossier is confidential and intended for investment committee use.
</div>
</body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
}

export default function Valuations() {
  const [valuations, setValuations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [params] = useSearchParams();
  const vid = params.get("vid");

  const load = async () => {
    setLoading(true);
    try {
      const r = await endpoints.valuations();
      setValuations(r.data);
      const target = vid ? r.data.find((v) => v.id === vid) : r.data[0];
      if (target) pickValuation(target.id);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const pickValuation = async (id) => {
    setOpening(true);
    try {
      let r = await endpoints.getValuation(id);
      // Poll if still generating
      let tries = 0;
      while (r.data.status === "generating" && tries < 30) {
        await new Promise((res) => setTimeout(res, 4000));
        r = await endpoints.getValuation(id);
        tries += 1;
      }
      setSelected(r.data);
    } finally { setOpening(false); }
  };

  return (
    <div className="p-4 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="CLARA Core · Valuations"
        title="Institutional Dossiers"
        description="Investment-banking grade valuations with 40-page M&A dossiers — generated automatically by CLARA AI."
        testid="valuations-page"
      />

      {loading ? <Spinner /> :
       !valuations.length ? <EmptyState icon={Calculator} title="No valuations yet" description={'Head to Companies and click "Valuate" to generate your first dossier.'} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 space-y-2 max-h-[80vh] overflow-auto">
            {valuations.map((v) => (
              <button
                key={v.id}
                onClick={() => pickValuation(v.id)}
                data-testid={`valuation-item-${v.id}`}
                className={`w-full text-left clara-card p-4 transition ${selected?.id === v.id ? "border-[#C5A059]/50" : ""}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="font-medium text-[13px] flex-1 pr-2">{v.company_name}</div>
                  <Badge tone={v.deal_feasibility === "proceed" ? "green" : v.deal_feasibility === "reject" ? "red" : "yellow"}>
                    {v.deal_feasibility}
                  </Badge>
                </div>
                <div className="text-[11px] text-white/50 font-mono">EV {fmt.money(v.enterprise_value)} · {v.ev_ebitda_multiple}x</div>
                <div className="text-[10px] text-white/30 mt-1">{fmt.date(v.created_at)}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            {opening ? <Card><Spinner /></Card> :
             selected ? (
              <Card>
                <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                  <div>
                    <div className="overline">{selected.dossier_type === "investor_profile" ? "Investor Profile Dossier" : "M&A Dossier"}</div>
                    <h2 className="font-display text-3xl mt-1">{selected.company_name}</h2>
                    <div className="text-[12px] text-white/40 mt-1 flex items-center gap-2">
                      Generated {fmt.date(selected.created_at)}
                      {selected.status === "generating" && <Badge tone="yellow">AI enriching…</Badge>}
                      {selected.status === "ready" && <Badge tone="green">Ready</Badge>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => openDossier(selected)} data-testid="open-dossier-btn">
                      <ExternalLink size={14} /> Open in browser
                    </Button>
                    <a
                      href={endpoints.valuationPdfUrl(selected.id)}
                      target="_blank" rel="noopener noreferrer"
                      data-testid="download-pdf-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 text-[13px] rounded-md font-medium bg-[#C5A059] text-black hover:bg-[#D4AF37] transition"
                    >
                      <FileDown size={14} /> Download PDF
                    </a>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="clara-surface-2 p-3 rounded-md">
                    <div className="overline mb-1">{selected.dossier_type === "investor_profile" ? "Assets under Mgmt" : "Enterprise Value"}</div>
                    <div className="kpi-value text-xl text-gold">{fmt.money(selected.enterprise_value)}</div>
                  </div>
                  <div className="clara-surface-2 p-3 rounded-md">
                    <div className="overline mb-1">{selected.dossier_type === "investor_profile" ? "Max Ticket" : "Recommended Bid"}</div>
                    <div className="kpi-value text-xl">{fmt.money(selected.recommended_bid)}</div>
                  </div>
                  <div className="clara-surface-2 p-3 rounded-md">
                    <div className="overline mb-1">{selected.dossier_type === "investor_profile" ? "Dossier Type" : "EV/EBITDA"}</div>
                    <div className="kpi-value text-xl">{selected.dossier_type === "investor_profile" ? "Investor" : `${selected.ev_ebitda_multiple}x`}</div>
                  </div>
                  <div className="clara-surface-2 p-3 rounded-md">
                    <div className="overline mb-1">{selected.dossier_type === "investor_profile" ? "Engagement" : "Credit Rating"}</div>
                    <div className="kpi-value text-xl" style={{ color: selected.credit_rating.startsWith("A") ? "#34d399" : selected.credit_rating.startsWith("B") ? "#facc15" : "#f87171" }}>
                      {selected.dossier_type === "investor_profile" ? selected.deal_feasibility : selected.credit_rating}
                    </div>
                  </div>
                </div>

                <div className="clara-surface-2 p-4 rounded-md mb-5 border-l-2 border-[#C5A059]">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={14} className="text-gold" />
                    <div className="overline">Investment Verdict</div>
                  </div>
                  <div className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{selected.summary}</div>
                </div>

                <div className="text-[11px] text-white/40 flex items-center gap-2">
                  <FileText size={12} /> Dossier: {selected.dossier_html?.length > 5000 ? "Full institutional content available" : "Generating..."}
                </div>
              </Card>
            ) : <Card><div className="text-white/40 py-10 text-center">Select a valuation from the list.</div></Card>}
          </div>
        </div>
      )}
    </div>
  );
}
